import type { ReactNode } from "react";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";
import { siteSurveyOverview } from "../screens/SiteSurvey.jsx";

/**
 * The Survey page's "Site survey" card: every section with how far it has
 * got, each a link to its page (migration 0056). What the installer needs on
 * the day, beside the heat loss — so the survey page says in one place what
 * is still to do.
 */
export function SiteSurveyCard({ surveyId }: { readonly surveyId: string }): ReactNode {
  const { client } = usePortal();
  // `async` so an older client or API fails this card, not the page.
  const { state } = useAsync(async () => client.siteSurvey(surveyId), [client, surveyId]);
  if (state.status !== "ready") return null;
  const rows = siteSurveyOverview(state.data);
  const required = rows.reduce((s, r) => s + r.required, 0);
  const answered = rows.reduce((s, r) => s + r.answered, 0);
  const next = rows.find((r) => !r.complete) ?? rows.find((r) => !r.touched);
  return (
    <section className="site-survey-card" data-testid="site-survey-card" aria-labelledby="site-survey-card-title">
      <div className="site-survey-card__head">
        <h2 id="site-survey-card-title">Site survey</h2>
        <span className="muted small">
          {answered} of {required} required answered
        </span>
        <div className="site-survey__bar site-survey-card__bar" aria-hidden="true">
          <span style={{ width: `${required === 0 ? 100 : Math.round((answered / required) * 100)}%` }} />
        </div>
        {next ? (
          <a className="cta" href={href.surveySite(surveyId, next.section.id)}>
            {next.touched ? "Carry on" : "Start"}: {next.section.title}
          </a>
        ) : null}
      </div>
      <div className="site-survey-card__rows">
        {rows.map((r) => {
          const tone = r.required === 0 ? (r.touched ? "done" : "optional") : r.complete ? "done" : r.touched ? "part" : "todo";
          const words =
            r.required === 0
              ? r.touched
                ? "Answered"
                : "Optional"
              : r.complete
                ? "Done"
                : `${r.required - r.answered} to answer`;
          return (
            <a key={r.section.id} className="site-survey-card__row" href={href.surveySite(surveyId, r.section.id)}>
              <span className="site-survey-card__name">{r.section.title}</span>
              <span className="site-survey-card__blurb muted small">{r.section.blurb}</span>
              <span className={`site-survey-card__status site-survey-card__status--${tone}`}>{words}</span>
            </a>
          );
        })}
      </div>
    </section>
  );
}
